import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Alert,
  ScrollView,
} from 'react-native';
import type { CareRole } from '../services/unifiedAuthService';

interface CareRoleSelectionScreenProps {
  onBack: () => void;
  onCareRoleSelected: (careRole: CareRole) => void;
}

const CARE_ROLES: { value: CareRole; label: string; description: string; emoji: string }[] = [
  { value: 'family', label: 'Family / Coordinator', description: 'Coordinate care for a loved one, manage visits and care team', emoji: '👨‍👩‍👧‍👦' },
  { value: 'senior', label: 'Senior / Care Recipient', description: 'View your schedule and care visits', emoji: '🧓' },
  { value: 'professional', label: 'Care Professional', description: 'Manage your visits, check-in and updates', emoji: '🩺' },
];

const CareRoleSelectionScreen: React.FC<CareRoleSelectionScreenProps> = ({
  onBack,
  onCareRoleSelected,
}) => {
  const [selectedRole, setSelectedRole] = useState<CareRole | null>(null);

  const handleContinue = () => {
    if (!selectedRole) {
      Alert.alert('Please Select', 'Please select your role in care coordination.');
      return;
    }
    onCareRoleSelected(selectedRole);
  };

  return (
    <ScrollView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity onPress={onBack} style={styles.backButton}>
          <Text style={styles.backButtonText}>← Back</Text>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Care Coordination</Text>
        <View style={{ width: 50 }} />
      </View>

      <View style={styles.content}>
        <Text style={styles.title}>How will you use care coordination?</Text>
        <Text style={styles.subtitle}>
          Choose the role that best describes you. You'll create a user account with care features.
        </Text>

        <View style={styles.optionsContainer}>
          {CARE_ROLES.map((role) => (
            <TouchableOpacity
              key={role.value}
              style={[
                styles.optionCard,
                selectedRole === role.value && styles.selectedOptionCard,
              ]}
              onPress={() => setSelectedRole(role.value)}
            >
              <Text style={styles.optionEmoji}>{role.emoji}</Text>
              <View style={styles.optionContent}>
                <Text style={styles.optionTitle}>{role.label}</Text>
                <Text style={styles.optionDescription}>{role.description}</Text>
              </View>
              {selectedRole === role.value && (
                <View style={styles.selectedIndicator}>
                  <Text style={styles.selectedCheckmark}>✓</Text>
                </View>
              )}
            </TouchableOpacity>
          ))}
        </View>

        <TouchableOpacity
          style={[styles.continueButton, !selectedRole && styles.continueButtonDisabled]}
          onPress={handleContinue}
          disabled={!selectedRole}
        >
          <Text style={[
            styles.continueButtonText,
            !selectedRole && styles.continueButtonTextDisabled,
          ]}>
            Continue
          </Text>
        </TouchableOpacity>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f8f9fa' },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingTop: 60,
    paddingBottom: 20,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e1e5e9',
  },
  backButton: { padding: 10 },
  backButtonText: { fontSize: 16, color: '#007AFF', fontWeight: '500' },
  headerTitle: { fontSize: 20, fontWeight: 'bold', color: '#1c1c1e' },
  content: { flex: 1, padding: 20 },
  title: { fontSize: 24, fontWeight: 'bold', color: '#1c1c1e', textAlign: 'center', marginBottom: 8 },
  subtitle: { fontSize: 15, color: '#666', textAlign: 'center', marginBottom: 24, lineHeight: 22 },
  optionsContainer: { gap: 12, marginBottom: 24 },
  optionCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    padding: 16,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: '#e1e5e9',
  },
  selectedOptionCard: { borderColor: '#007AFF', backgroundColor: '#f0f7ff' },
  optionEmoji: { fontSize: 32, marginRight: 12 },
  optionContent: { flex: 1 },
  optionTitle: { fontSize: 17, fontWeight: '600', color: '#1c1c1e', marginBottom: 4 },
  optionDescription: { fontSize: 14, color: '#666' },
  selectedIndicator: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#007AFF',
    alignItems: 'center',
    justifyContent: 'center',
  },
  selectedCheckmark: { color: '#fff', fontWeight: 'bold' },
  continueButton: {
    backgroundColor: '#007AFF',
    padding: 16,
    borderRadius: 12,
    alignItems: 'center',
  },
  continueButtonDisabled: { backgroundColor: '#c7d2e0' },
  continueButtonText: { color: '#fff', fontSize: 17, fontWeight: '600' },
  continueButtonTextDisabled: { color: '#8e9aa8' },
});

export default CareRoleSelectionScreen;
